import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import API from '../api';
import { useAuth } from '../contexts/AuthContext';
import { IconYouTube, IconFilm, IconEye, IconHeart, IconComment } from '../components/Icons';

function GrowthBadge({ pct }) {
  if (pct === null || pct === undefined) return null;
  const up = pct >= 0;
  return (
    <span style={{
      display:'inline-flex', alignItems:'center', gap:3,
      fontSize:11, fontWeight:600, padding:'2px 7px', borderRadius:4,
      background: up ? 'var(--accent-dim)' : 'rgba(255,68,68,0.08)',
      color: up ? 'var(--accent)' : '#ff4444',
      marginLeft:8,
    }}>
      {up ? '↑' : '↓'} {Math.abs(pct)}%
    </span>
  );
}


function KickSetup({ onSaved }) {
  const [platform, setPlatform] = useState('kick');
  const [username, setUsername] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(null); // null | channel object | 'error'
  const [saving, setSaving] = useState(false);
  const [verificationCode, setVerificationCode] = useState(null);
  const [verifyStep, setVerifyStep] = useState('input'); // 'input' | 'chat' | 'done'
  const [checking, setChecking] = useState(false);
  const [checkInterval, setCheckInterval] = useState(null);
  const [timeLeft, setTimeLeft] = useState(300);
  const [showSaveAnyway, setShowSaveAnyway] = useState(false);

  const verify = async () => {
    if (!username.trim()) return;
    setVerifying(true);
    setVerified(null);
    try {
      if (platform === 'kick') {
        // First confirm channel exists
        const res = await API.get(`/api/kick/verify/${username.trim().toLowerCase()}`);
        if (res.data.apiUnavailable) {
          // API down — save directly without chat verification
          setVerified(res.data);
          await API.post('/api/streamers', { slug: username.trim().toLowerCase(), display_name: verified?.display_name || username.trim(), platform: 'kick', kick_verified: false });
          onSaved();
        } else if (res.data.verified) {
          // Channel found — start chat verification
          const v = await API.post('/api/kick/start-verification', { slug: username.trim().toLowerCase() });
          setVerificationCode(v.data.code);
          setVerifyStep('chat');
          setTimeLeft(300);
          setVerified(res.data);
          // Start client-side WebSocket verification
          startClientSideVerification(username.trim().toLowerCase(), v.data.code);
          // Countdown timer
          const countdown = setInterval(() => setTimeLeft(t => { if (t <= 1) { clearInterval(countdown); } if (t <= 270) { setShowSaveAnyway(true); } return t - 1; }), 1000);
        } else {
          setVerified('error');
        }
      } else {
        setVerified({ display_name: username.trim(), slug: username.trim().toLowerCase(), platform: 'twitch' });
      }
    } catch {
      setVerified('error');
    } finally {
      setVerifying(false);
    }
  };

  const startClientSideVerification = (slug, code) => {
    // Connect directly to Kick's Pusher from the browser — avoids server IP blocks
    const getChannelId = async () => {
      try {
        const r = await fetch(`https://kick.com/api/v2/channels/${slug}`);
        if (r.ok) { const d = await r.json(); return d?.chatroom?.id || d?.id; }
      } catch {}
      try {
        const r = await fetch(`https://kick.com/api/v1/channels/${slug}`);
        if (r.ok) { const d = await r.json(); return d?.chatroom?.id || d?.id; }
      } catch {}
      return null;
    };

    getChannelId().then(chatroomId => {
      if (!chatroomId) { console.log('No chatroom ID for', slug); return; }
      console.log('Connecting to chatroom', chatroomId);

      const ws = new WebSocket('wss://ws-us2.pusher.com/app/32cbd69e4b950bf97679?protocol=7&client=js&version=7.4.0&flash=false');

      ws.onopen = () => {
        ws.send(JSON.stringify({ event: 'pusher:subscribe', data: { auth: '', channel: `chatrooms.${chatroomId}.v2` } }));
        console.log('Subscribed to Kick chatroom', chatroomId);
      };

      ws.onmessage = async (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.event?.includes('ChatMessage') || msg.event?.includes('chat_message')) {
            const data = typeof msg.data === 'string' ? JSON.parse(msg.data) : msg.data;
            const text = (data?.content || data?.message || '').toUpperCase();
            console.log('Chat:', text.slice(0, 40));
            if (text.includes(code.toUpperCase())) {
              ws.close();
              if (checkInterval) clearInterval(checkInterval);
              // Save as verified
              await API.post('/api/streamers', { slug, display_name: verified?.display_name || slug, platform: 'kick', kick_verified: true });
              await API.post('/api/kick/mark-verified', { slug, chatroomId, channelId: verified?.channelId }).catch(() => {});
              setVerifyStep('connected');
              setTimeout(() => onSaved(), 2000);
            }
          }
        } catch {}
      };

      ws.onerror = (e) => console.log('WS error:', e);

      // Store ws ref for cleanup
      window._kickVerifyWs = ws;
    });
  };

  const checkVerification = async (slug) => {
    // No-op — verification now handled client-side
  };

  const save = async () => {
    setSaving(true);
    try {
      await API.post('/api/streamers', {
        slug: verified.slug || username.trim().toLowerCase(),
        display_name: verified.display_name || username.trim(),
        platform,
        kick_verified: platform === 'kick',
      });
      onSaved();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, padding:'24px', marginBottom:24}}>
      <div style={{fontFamily:'var(--font-display)', fontWeight:700, fontSize:16, marginBottom:4}}>Connect your streaming account</div>
      <div style={{color:'var(--text-muted)', fontSize:13, marginBottom:20}}>Add your streaming account so Clump knows which channel to monitor. <span style={{color:'#ff9900'}}>Only add channels you own</span> — adding someone else's is against our Terms of Service.</div>

      {/* Platform selector */}
      <div style={{display:'flex', gap:8, marginBottom:16}}>
        {[{id:'kick', label:'Kick'}, {id:'twitch', label:'Twitch'}].map(p => (
          <button
            key={p.id}
            type="button"
            onClick={() => { setPlatform(p.id); setVerified(null); setUsername(''); }}
            style={{
              padding:'8px 20px', borderRadius:20, fontSize:13, fontWeight:500,
              cursor:'pointer', fontFamily:'var(--font-body)', transition:'all 0.15s',
              background: platform===p.id ? 'var(--accent)' : 'var(--surface2)',
              color: platform===p.id ? '#000' : 'var(--text-dim)',
              border: platform===p.id ? '1px solid var(--accent)' : '1px solid var(--border)',
            }}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Username input */}
      <div style={{display:'flex', gap:10, marginBottom: verified ? 16 : 0}}>
        <input
          value={username}
          onChange={e => { setUsername(e.target.value); setVerified(null); }}
          placeholder={platform === 'kick' ? 'Your Kick username' : 'Your Twitch username'}
          style={{flex:1, padding:'10px 14px', background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:6, color:'var(--text)', fontFamily:'var(--font-body)', fontSize:13, outline:'none'}}
          onKeyDown={e => e.key==='Enter' && verify()}
        />
        <button className="btn btn-ghost" style={{width:'auto', fontSize:13}} onClick={verify} disabled={verifying || !username.trim()}>
          {verifying ? 'Checking...' : 'Verify'}
        </button>
      </div>

      {/* Verification result */}
      {verified === 'error' && (
        <div style={{fontSize:13, color:'#ff4444', padding:'10px 14px', background:'rgba(255,68,68,0.06)', borderRadius:6, marginBottom:12}}>
          Channel not found on Kick. Check the username and try again.
        </div>
      )}

      {/* Chat verification step */}
      {verifyStep === 'chat' && verificationCode && (
        <div style={{background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:8, padding:16, marginBottom:12}}>
          <div style={{fontFamily:'var(--font-display)', fontWeight:700, fontSize:14, marginBottom:8}}>
            Prove it's your account
          </div>
          <div style={{fontSize:13, color:'var(--text-muted)', marginBottom:12, lineHeight:1.6}}>
            Type this code in your Kick chat — you don't need to be live. Clump will detect it automatically.
          </div>
          <div style={{
            fontFamily:'monospace', fontSize:20, fontWeight:700, letterSpacing:3,
            color:'var(--accent)', background:'var(--surface)', padding:'12px 16px',
            borderRadius:6, textAlign:'center', marginBottom:12, border:'1px solid var(--border)'
          }}>
            {verificationCode}
          </div>
          <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', fontSize:12, color:'var(--text-muted)'}}>
            <span>{checking ? '👀 Watching chat...' : '⏳ Waiting for code in chat...'}</span>
            <span>{Math.floor(timeLeft/60)}:{String(timeLeft%60).padStart(2,'0')} remaining</span>
          </div>
          {showSaveAnyway && timeLeft > 0 && (
            <div style={{marginTop:10, display:'flex', alignItems:'center', justifyContent:'space-between'}}>
              <span style={{fontSize:12, color:'var(--text-muted)'}}>Taking too long?</span>
              <button
                onClick={async () => {
                  if (checkInterval) clearInterval(checkInterval);
                  setSaving(true);
                  await API.post('/api/streamers', { slug: username.trim().toLowerCase(), display_name: verified?.display_name || username.trim(), platform: 'kick', kick_verified: false });
                  onSaved();
                }}
                style={{background:'none', border:'1px solid var(--border)', color:'var(--text-muted)', cursor:'pointer', fontSize:12, padding:'4px 10px', borderRadius:4, fontFamily:'var(--font-body)'}}
              >
                Save without verifying
              </button>
            </div>
          )}
          {timeLeft <= 0 && (
            <div style={{marginTop:10, fontSize:12, color:'#ff4444'}}>
              Code expired. <button onClick={() => { setVerifyStep('input'); setVerified(null); setVerificationCode(null); setShowSaveAnyway(false); }} style={{background:'none', border:'none', color:'var(--accent)', cursor:'pointer', fontSize:12}}>Try again</button>
            </div>
          )}
        </div>
      )}

      {/* Connected success */}
      {verifyStep === 'connected' && (
        <div style={{background:'var(--accent-dim)', border:'1px solid rgba(200,241,53,0.3)', borderRadius:8, padding:'16px 20px', marginBottom:12, display:'flex', alignItems:'center', gap:12}}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
          <div>
            <div style={{fontFamily:'var(--font-display)', fontWeight:700, color:'var(--accent)', fontSize:15}}>Connected!</div>
            <div style={{fontSize:12, color:'var(--text-muted)', marginTop:2}}>Your Kick account has been verified.</div>
          </div>
        </div>
      )}

      {/* Twitch save button */}
      {platform === 'twitch' && verified && verified !== 'error' && (
        <button className="btn btn-primary" style={{width:'100%'}} onClick={save} disabled={saving}>
          {saving ? 'Saving...' : 'Connect Twitch account →'}
        </button>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats]             = useState({ pending:0, scheduled:0, posted:0, rejected:0 });
  const [kickSlug, setKickSlug]       = useState('');
  const [kickInput, setKickInput]     = useState('');
  const [kickSaving, setKickSaving]   = useState(false);
  const [kickSet, setKickSet]         = useState(null); // null=loading, true=set, false=not set
  const [channel, setChannel]         = useState(null);
  const [channelLoading, setChannelLoading] = useState(true);
  const [showYoutubeBanner]           = useState(() => window.location.search.includes('youtube=connected'));
  const [showTwitchBanner]            = useState(() => window.location.search.includes('twitch=connected'));
  const [youtubeConnected, setYoutubeConnected] = useState(null);

  useEffect(() => {
    API.get('/api/clips').then(res => {
      const clips = res.data;
      setStats({
        pending:   clips.filter(c => c.status === 'pending').length,
        scheduled: clips.filter(c => c.status === 'approved' || c.status === 'approved_auto').length,
        posted:    clips.filter(c => c.status === 'posted').length,
        rejected:  clips.filter(c => c.status === 'rejected').length,
      });
    }).catch(() => {});

    API.get('/api/auth/youtube/status').then(r => setYoutubeConnected(r.data.connected)).catch(() => {});

    API.get('/api/analytics/channel').then(r => {
      setChannel(r.data);
      setChannelLoading(false);
    }).catch(() => setChannelLoading(false));

    API.get('/api/streamers').then(r => {
      const streamer = r.data?.[0];
      // Show banner if not verified (regardless of slug)
      const isVerified = streamer?.kick_verified === true;
      setKickSet(isVerified);
      if (streamer?.slug) setKickSlug(streamer.slug);
    }).then(async () => {
      // Silently try to reverify in background if not verified
      try { await API.post('/api/kick/reverify'); } catch {}
    }).catch(() => setKickSet(false));
  }, []);

  const connectYoutube = async () => {
    try { const res = await API.get('/api/auth/youtube'); window.location.href = res.data.url; }
    catch (err) { console.error(err); }
  };

  const saveKick = async () => {
    if (!kickInput.trim()) return;
    setKickSaving(true);
    try {
      await API.post('/api/streamers', { slug: kickInput.trim().toLowerCase(), display_name: kickInput.trim() });
      setKickSet(true);
      setKickSlug(kickInput.trim().toLowerCase());
    } catch (err) {
      console.error(err);
    } finally {
      setKickSaving(false);
    }
  };

  const fmt = n => typeof n === 'number' ? n.toLocaleString() : '—';

  const metrics = channel ? [
    {
      label: 'Views',
      value: fmt(channel.period28.views),
      growth: channel.growth.views,
      sub: 'last 28 days vs prev 28',
    },
    {
      label: 'Likes',
      value: fmt(channel.period28.likes),
      growth: channel.growth.likes,
      sub: 'last 28 days vs prev 28',
    },
    {
      label: 'Subscribers gained',
      value: fmt(channel.period28.subscribers),
      growth: channel.growth.subscribers,
      sub: 'last 28 days vs prev 28',
    },
  ] : [];

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Hey, {user?.first_name || user?.display_name?.split(' ')[0] || 'there'}!</div>
        <div className="page-subtitle">Here's what's happening with your channel</div>
      </div>

      {youtubeConnected === false && !showYoutubeBanner && (
        <div style={{background:'rgba(255,68,68,0.05)', border:'1px solid rgba(255,68,68,0.15)', borderRadius:12, padding:'20px 24px', marginBottom:24, display:'flex', alignItems:'center', justifyContent:'space-between'}}>
          <div>
            <div style={{fontFamily:'var(--font-display)', fontWeight:700, fontSize:16}}>Connect your YouTube channel</div>
            <div style={{color:'var(--text-muted)', fontSize:13, marginTop:2}}>Required to post clips automatically</div>
          </div>
          <button className="btn btn-primary" style={{width:'auto', display:'flex', alignItems:'center', gap:6}} onClick={connectYoutube}>
            <IconYouTube color="#000" /> Connect YouTube
          </button>
        </div>
      )}

      {showYoutubeBanner && (
        <div style={{background:'var(--accent-dim)', border:'1px solid rgba(200,241,53,0.2)', borderRadius:12, padding:'16px 24px', marginBottom:24, display:'flex', alignItems:'center', gap:12}}>
          <IconYouTube /><div style={{color:'var(--accent)', fontWeight:600, fontSize:14}}>YouTube channel connected successfully</div>
        </div>
      )}
      {showTwitchBanner && (
        <div style={{background:'var(--accent-dim)', border:'1px solid rgba(200,241,53,0.2)', borderRadius:12, padding:'16px 24px', marginBottom:24, display:'flex', alignItems:'center', gap:12}}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="var(--accent)" xmlns="http://www.w3.org/2000/svg"><path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z"/></svg>
          <div style={{color:'var(--accent)', fontWeight:600, fontSize:14}}>Twitch account connected successfully</div>
        </div>
      )}

      {kickSet === false && (
        <KickSetup onSaved={() => setKickSet(true)} />
      )}

      {stats.pending > 0 && (
        <div style={{background:'var(--accent-dim)', border:'1px solid rgba(200,241,53,0.2)', borderRadius:12, padding:'20px 24px', marginBottom:32, display:'flex', alignItems:'center', justifyContent:'space-between'}}>
          <div>
            <div style={{fontFamily:'var(--font-display)', fontWeight:700, color:'var(--accent)', fontSize:16}}>
              {stats.pending} clip{stats.pending !== 1 ? 's' : ''} waiting for review
            </div>
            <div style={{color:'var(--text-muted)', fontSize:13, marginTop:2}}>Approve or reject them to keep your calendar moving</div>
          </div>
          <Link to="/clips" className="btn btn-approve">Review now →</Link>
        </div>
      )}

      {/* Channel analytics */}
      {youtubeConnected && (
        <>
          {channelLoading ? (
            <div style={{color:'var(--text-muted)', fontSize:14, marginBottom:32}}>Loading channel data...</div>
          ) : channel ? (
            <>
              {/* Channel analytics — top row, no title */}
              <div style={{display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16, marginBottom:24}}>
                {metrics.map((m, i) => (
                  <div key={i} style={{background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, padding:24}}>
                    <div style={{fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.1em', color:'var(--text-muted)', marginBottom:10}}>
                      {m.label}
                    </div>
                    <div style={{display:'flex', alignItems:'baseline', flexWrap:'wrap', gap:4, marginBottom:8}}>
                      <div style={{fontFamily:'var(--font-display)', fontSize:42, fontWeight:800, letterSpacing:'-2px', color:'var(--accent)'}}>{m.value}</div>
                      <GrowthBadge pct={m.growth} />
                    </div>
                    <div style={{fontSize:11, color:'var(--text-muted)'}}>{m.sub}</div>
                  </div>
                ))}
              </div>

              {/* Clip counts — middle row with title */}
              <div className="section-title" style={{marginBottom:12}}>Clips</div>
              <div className="stats-row" style={{marginBottom:24}}>
                <div className="stat-card"><div className="stat-label">Pending Review</div><div className="stat-value">{stats.pending}</div></div>
                <div className="stat-card"><div className="stat-label">Scheduled</div><div className="stat-value">{stats.scheduled}</div></div>
                <div className="stat-card"><div className="stat-label">Posted</div><div className="stat-value">{stats.posted}</div></div>
                <div className="stat-card"><div className="stat-label">Rejected</div><div className="stat-value">{stats.rejected}</div></div>
              </div>

              {channel.recentClips?.length > 0 && (
                <>
                  <div className="section-title" style={{marginBottom:16}}>Recently Posted</div>
                  <div className="clips-grid">
                    {channel.recentClips.map(clip => (
                      <div key={clip.id} className="clip-card">
                        {clip.s3_url
                          ? <video className="clip-video" src={clip.s3_url} controls />
                          : clip.thumbnail_url
                            ? <img className="clip-video" src={clip.thumbnail_url} alt="thumbnail" style={{objectFit:'cover'}} />
                            : <div className="clip-video-placeholder"><IconFilm width={32} height={32} color="#444" /></div>
                        }
                        <div className="clip-body">
                          <div style={{fontFamily:'var(--font-display)', fontWeight:700, fontSize:14, marginBottom:12}}>
                            {clip.custom_title || clip.ai_title}
                          </div>
                          <div style={{display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8, marginBottom:10, padding:'10px 12px', background:'var(--surface2)', borderRadius:8}}>
                            {[
                              { icon: <IconEye width={12} height={12}/>, label:'Views', val: clip.views },
                              { icon: <IconHeart width={12} height={12}/>, label:'Likes', val: clip.likes },
                              { icon: <IconComment width={12} height={12}/>, label:'Comments', val: clip.comments },
                            ].map(({icon, label, val}) => (
                              <div key={label} style={{textAlign:'center'}}>
                                <div style={{fontFamily:'var(--font-display)', fontWeight:800, fontSize:22, color:'var(--accent)'}}>{(val||0).toLocaleString()}</div>
                                <div style={{fontSize:11, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.05em', marginTop:3, display:'flex', alignItems:'center', justifyContent:'center', gap:3}}>{icon}{label}</div>
                              </div>
                            ))}
                          </div>
                          <div style={{fontSize:11, color:'var(--text-muted)', marginBottom:8}}>
                            Posted {new Date(clip.posted_at).toLocaleDateString('en-GB', {day:'numeric', month:'short'})}
                          </div>
                          {clip.youtube_url && (
                            <a href={clip.youtube_url} target="_blank" rel="noreferrer" className="btn btn-ghost" style={{width:'100%', fontSize:12, display:'flex', alignItems:'center', justifyContent:'center', gap:5}}>
                              View on YouTube →
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <div style={{color:'var(--text-muted)', fontSize:14, marginBottom:32}}>Could not load channel data.</div>
          )}
        </>
      )}
    </div>
  );
}
